import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import '../styles/handsontable.css'; // Import generic HandsOnTable styles
import { Box, Flex, useToast } from '@chakra-ui/react';
import SubmitButton from './SubmitButton';
import { getAgraphySectionsData, updateAgraphySectionsData } from '../services/AgraphyLandingSections';
import FormLoader from './FormLoader';
import { DataNotFound } from './DataNotFound';
import { showErrorToast, showInfoToast, showSuccessToast } from '../util/errorHandler';


const Sections = ({ onCountLoad }) => {
  const { alias } = useParams();
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]); // Store original data with all keys
  const [changedRecords, setChangedRecords] = useState(new Map()); // Track changes by row index
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hotRef = useRef(null);
  const toast = useToast();

  // Handle individual cell changes in HandsOnTable
  const handleTableChange = useCallback((changes, source) => {
    if (source === 'edit' && changes) {
      const newChangedRecords = new Map(changedRecords);
      
      changes.forEach(([row, col, oldValue, newValue]) => {
        // Skip S.No. column (col === 0)
        if (col === 0) return;
        
        // Only track if value actually changed
        if (oldValue !== newValue) {
          const rowKey = row;
          const columnHeader = columnHeaders[col - 1]; // Adjust index due to S.No. column
          
          if (!newChangedRecords.has(rowKey)) {
            // Initialize record with all original data and changed field
            newChangedRecords.set(rowKey, {
              ...originalData[row], // Start with complete original data
              [columnHeader]: newValue
            });
          } else {
            // Add/update field in existing changed record
            const existingRecord = newChangedRecords.get(rowKey);
            existingRecord[columnHeader] = newValue;
            newChangedRecords.set(rowKey, existingRecord);
          }
        }
      });
      
      setChangedRecords(newChangedRecords);
    }
  }, [changedRecords, columnHeaders, originalData]);

  useEffect(() => {
    const getLandingPageData = async () => {
      setIsLoading(true);
      try {
        if (alias) {
          const result = await getAgraphySectionsData(alias);

          if (result?.data?.length > 0) {
            // Store original data with all keys
            setOriginalData(result.data);

            // Filter out _id and image_alias fields for display
            const allKeys = Object.keys(result.data[0]);
            const filteredKeys = allKeys.filter(key =>
              key !== '_id' && key !== 'image_alias'
            );
            setColumnHeaders(filteredKeys);

            // Transform data to include S.No. column and filter out unwanted fields for display
            const rowData = result.data.map((item) => [
              null, // Placeholder for S.No. column (will be calculated dynamically)
              ...filteredKeys.map((key) => item[key] ?? ""), // Extract values by key
            ]);

            setTableData(rowData);
            onCountLoad?.(result.data.length);
          } else {
            onCountLoad?.(0);
          }
        }
      } catch (error) {
        console.error('Error fetching film data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getLandingPageData();
  }, [alias]); // eslint-disable-line react-hooks/exhaustive-deps

  // const handleAutoSave = async (changes, source) => {
  //   if (source === "edit") {
  //     // Iterate over the changes array
  //     for (const [row, prop, oldValue, newValue] of changes) {
  //       // If the value has changed (old value is different from new value)
  //       if (oldValue !== newValue) {
  //         // Prepare the data to be sent in the API request
  //         const data = {
  //           "_id": tableData[row]._id,
  //           [prop]: newValue,
  //         };

  //         // Make the API call to update the data
  //         try {
  //           setIsSubmitting(true);
  //           await updateAgraphySectionsData(alias, data);
  //         } catch (error) {
  //           console.error("Error saving data:", error);
  //         } finally {
  //           setIsSubmitting(false);
  //         }
  //       }
  //     }
  //   }
  // };

  const saveClickCallback = async () => {
    // Check if there are any changes to save
    if (changedRecords.size === 0) {
            showInfoToast(toast, "No changes to save");
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Convert Map to array of changed records only
      const updatedRecords = Array.from(changedRecords.values());
      
      const result = await updateAgraphySectionsData(alias, updatedRecords);

      if (result?.status === 200) {
        // Clear the changed records after successful save
        setChangedRecords(new Map());
        
        // Update original data with the saved changes
        const newOriginalData = [...originalData];
        changedRecords.forEach((changedRecord, rowIndex) => {
          newOriginalData[rowIndex] = changedRecord;
        });
        setOriginalData(newOriginalData);
        
                showSuccessToast(toast, "Data saved successfully");
      }
    } catch (error) {
      console.error("Error saving data:", error);
            showErrorToast(toast, "Failed to save data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hotSettings = useMemo(() => ({
    data: tableData,
    colHeaders: ['S.No.', ...columnHeaders], // Add S.No. as first column
    height: "auto",
    width: "auto",
    afterChange: handleTableChange, // Track changes in real-time
    licenseKey: "non-commercial-and-evaluation",
    autoWrapRow: true,
    contextMenu: true,
    cells: (row, col) => {
      if (col === 0) return { readOnly: true }; // S.No. column is read-only
      
      // Make alias column read-only
      const columnHeader = columnHeaders[col - 1]; // Adjust index due to S.No. column
      if (columnHeader === 'alias') {
        return { readOnly: true };
      }
      
      return {}; // Default for other columns (editable)
    },
    columns: [
      // S.No. column
      {
        data: 0,
        title: 'S.No.',
        width: 60,
        readOnly: true,
        renderer: (instance, td, row, col, prop, value, cellProperties) => {
          // Calculate serial number based on row
          const serialNumber = row + 1;
          td.innerHTML = serialNumber;
          td.style.textAlign = 'center';
          td.style.fontWeight = 'bold';
          td.style.backgroundColor = '#f8f9fa';
          td.style.borderRight = '2px solid #dee2e6';
          td.style.color = '#495057';
          td.style.height = '30px';
          return td;
        }
      },
      // Map existing columns (shifted by 1 due to S.No. column)
      ...columnHeaders.map((header, index) => {
        const columnConfig = {
          data: index + 1, // Shift data index by 1
          title: header,
          readOnly: header === 'alias' // Only alias column is read-only
        };
        
        // Special configuration for isVisible column
        if (header === 'isVisible') {
          columnConfig.type = 'checkbox';
          columnConfig.checkedTemplate = true;
          columnConfig.uncheckedTemplate = false;
        }
        
        // Calculate width based on header text length or max 300px
        const headerTextWidth = header.length * 8 + 140; // Approximate character width + padding
        const columnWidth = Math.min(headerTextWidth, 300);
        
        // Add width and text wrapping configuration for all columns
        columnConfig.width = columnWidth;
        columnConfig.wordWrap = true;
        
        // Only add custom renderer for non-checkbox columns
        if (header !== 'isVisible') {
          columnConfig.renderer = (instance, td, row, col, prop, value, cellProperties) => {
            // Set cell properties for text wrapping
            td.style.whiteSpace = 'normal';
            td.style.wordWrap = 'break-word';
            td.style.maxWidth = `${columnWidth}px`;
            td.style.height = 'auto';
            td.style.padding = '8px';
            td.style.verticalAlign = 'top';
            
            // Set the cell content
            td.innerHTML = value || '';
            return td;
          };
        }
        
        return columnConfig;
      })
    ]
  }), [tableData, columnHeaders, handleTableChange]);

  return (
    <Box maxW="auto">
      {isLoading ? (
        <FormLoader />
      ) : (
        <>
          {tableData?.length > 0 ? (
            <>
              <Flex justifyContent="space-between" mb={4} alignItems="center">
                <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
                {changedRecords.size > 0 && (
                  <Box color="orange.500" fontSize="sm">
                    {changedRecords.size} record(s) modified - Click Save to update
                  </Box>
                )}
              </Flex>
              
              {/* Handsontable */}
              <HotTable
                ref={hotRef}
                {...hotSettings}
              />
              
              <Flex justifyContent="flex-start" mt={4}>
                <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
              </Flex>
            </>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
    </Box>
  );
};

export default Sections;